import { liveReload } from '@shared/services/realtime.service';
import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MaterialModule } from '@app/material.module';

import { UserManualsService } from '@shared/services/entities/userManuals.service';
import { UsersService } from '@shared/services/entities/users.service';
import { RolesService } from '@shared/services/entities/roles.service';
import { ModulesService } from '@shared/services/modules.service';
import { UploadDownloadService } from '@shared/services/upload-download.service';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';
import { FileManagerDialogComponent } from '@shared/components/file-manager-dialog/file-manager-dialog.component';

import { Role } from '@shared/models/entities/roles.model';
import { UserManual, ManualVideo } from '@shared/models/entities/userManual.model';
import { isEpioneAdmin } from '@shared/utils/epione-admin';
import { openS3File } from '@shared/utils/open-s3-file';
import { sortAlphabetically } from '@shared/utils/sort.utils';

import {
  ManualVideoDialogComponent,
  ManualModuleOption,
} from './manual-video-dialog.component';

/** Videos de un módulo, ya agrupados para la vista. */
interface VideoGroup {
  module: string;
  videos: ManualVideo[];
}

@Component({
  selector: 'app-user-manuals',
  standalone: true,
  imports: [PageLoaderComponent, PageTitleComponent, FormsModule, ReactiveFormsModule, MaterialModule],
  templateUrl: './user-manuals.component.html',
  styleUrls: ['./user-manuals.component.scss'],
})
export class UserManualsComponent implements OnInit {
  /** Título = etiqueta visible del módulo (tabla `modules`). */
  readonly pageLabel = inject(ModulesService).moduleTitle('/support/manuals', 'Manual de usuarios');


  private manualsService = inject(UserManualsService);
  private usersService   = inject(UsersService);
  private rolesService   = inject(RolesService);
  private modulesService = inject(ModulesService);
  private uploads        = inject(UploadDownloadService);
  private message        = inject(CustomMessageService);
  private dialog         = inject(MatDialog);
  private router         = inject(Router);

  // ─── Estado ────────────────────────────────────────────────────────────
  readonly loading  = signal(false);
  readonly userRole = signal<Role | null>(null);

  /**
   * Roles listados en la barra lateral:
   *   · Administrador Epione → todos los roles del sistema.
   *   · Cualquier otro rol   → únicamente el suyo.
   */
  readonly roles = signal<Role[]>([]);
  readonly selectedRoleId = signal<string | null>(null);

  /** Manuales cargados, indexados por rol. */
  readonly manuals = signal<Record<string, UserManual>>({});

  readonly moduleOptions = signal<ManualModuleOption[]>([]);

  /** Solo el Administrador Epione puede cargar/editar manuales. */
  readonly canEdit = computed(() => isEpioneAdmin(this.userRole()));

  readonly selectedRole = computed<Role | null>(() =>
    this.roles().find(r => r.id === this.selectedRoleId()) ?? null
  );

  readonly selectedManual = computed<UserManual | null>(() => {
    const roleId = this.selectedRoleId();
    return roleId ? (this.manuals()[roleId] ?? null) : null;
  });

  /** Videos del manual seleccionado, agrupados por módulo. */
  readonly videoGroups = computed<VideoGroup[]>(() => {
    const videos = this.selectedManual()?.videos ?? [];
    const grouped = new Map<string, ManualVideo[]>();

    for (const video of videos) {
      const key = video.module || 'Sin módulo';
      const list = grouped.get(key) ?? [];
      list.push(video);
      grouped.set(key, list);
    }

    return Array.from(grouped.entries())
      .map(([module, list]) => ({ module, videos: sortAlphabetically(list, 'title') }))
      .sort((a, b) => a.module.localeCompare(b.module, 'es'));
  });

  readonly videoCount = computed(() => this.selectedManual()?.videos?.length ?? 0);

  /** Pantalla de carga del módulo: bloquea hasta terminar la carga inicial. */
  readonly pageLoad = new PageLoad();
  /** Tiempo real: recarga cuando otro usuario cambia estos módulos (WebSocket). */
  private readonly live = liveReload(['userManuals'], () => this.pageLoad.ready && this.pageLoad.refresh(this.loadManuals()));

  ngOnInit(): void {
    void this.init();
  }

  /** Carga inicial (también la usa "Reintentar"). */
  init(): Promise<boolean> {
    return this.pageLoad.run(() => this.initialLoad());
  }

  private async initialLoad(): Promise<void> {
    this.loading.set(true);
    try {
      await this.loadUserAndRole();
      await Promise.all([this.loadRoles(), this.loadModules()]);
      await this.loadManuals();
    } finally {
      this.loading.set(false);
    }
  }

  // ─── Carga de datos ────────────────────────────────────────────────────

  private async loadUserAndRole(): Promise<void> {
    if (!this.usersService.currentUser()) {
      try {
        await this.usersService.loadCurrentUser();
      } catch (err) {
        console.warn('[Manuales] No se pudo cargar el usuario:', err);
      }
    }

    const user = this.usersService.currentUser();
    if (!user?.role) {
      this.userRole.set(null);
      return;
    }

    try {
      await this.rolesService.search({ searchType: 'default', condition: { id: user.role } });
      const roles = this.rolesService.listData();
      this.userRole.set(roles.length > 0 ? (roles[0] as Role) : null);
    } catch (err) {
      console.warn('[Manuales] No se pudo cargar el rol:', err);
      this.userRole.set(null);
    }
  }

  private async loadRoles(): Promise<void> {
    // El rol no administrador no necesita (ni debe) ver la lista completa:
    // se queda con el suyo, que ya se cargó arriba.
    if (!this.canEdit()) {
      const own = this.userRole();
      this.roles.set(own ? [own] : []);
      this.selectedRoleId.set(own?.id ?? null);
      return;
    }

    try {
      const response = await this.rolesService.getAllRols();
      const all = sortAlphabetically((response.data ?? []) as Role[], 'name');
      this.roles.set(all);
      this.selectedRoleId.set(all.length > 0 ? all[0].id : null);
    } catch (err) {
      console.warn('[Manuales] No se pudieron cargar los roles:', err);
      this.roles.set([]);
    }
  }

  private async loadModules(): Promise<void> {
    let options: ManualModuleOption[] = [];

    try {
      await this.modulesService.search({
        searchType: 'default',
        condition: { deleted: false, active: true },
      });
      options = (this.modulesService.listData() ?? [])
        .filter(m => !!(m.label || m.name))
        .map(m => ({ id: m.id ?? null, label: m.label || m.name || '' }));
    } catch (err) {
      console.warn('[Manuales] No se pudieron cargar los módulos:', err);
    }

    if (options.length === 0) {
      // Mismo criterio que en tickets: sin la tabla `modules` sembrada, una
      // lista mínima evita que no se pueda clasificar el video.
      options = [
        'Tablero', 'Catálogos', 'Intervenciones', 'Inventario de productos',
        'Inventario de instrumental', 'Inventario de equipo', 'Reportes',
        'Módulos', 'Permisos', 'Roles', 'Usuarios', 'Soporte', 'General',
      ].map(label => ({ id: null, label }));
    } else {
      options = sortAlphabetically(options, 'label');
      options.push({ id: null, label: 'General' });
    }

    this.moduleOptions.set(options);
  }

  /** Carga los manuales de los roles visibles. */
  async loadManuals(): Promise<void> {
    const canEdit = this.canEdit();
    const roleId  = this.selectedRoleId();

    // Fail-closed: sin rol resuelto, un `list()` sin filtro traería los
    // manuales de TODOS los roles al navegador aunque la vista no los pinte.
    if (!canEdit && !roleId) {
      this.manuals.set({});
      return;
    }

    try {
      // El admin trae todos de un jalón; el resto solo el de su rol.
      const list = canEdit
        ? await this.manualsService.list()
        : await this.manualsService.list(roleId);

      const indexed: Record<string, UserManual> = {};
      for (const manual of list) {
        indexed[manual.role] = { ...manual, videos: manual.videos ?? [] };
      }
      this.manuals.set(indexed);
    } catch (err: any) {
      console.error('[Manuales] Error cargando manuales:', err);
      await this.message.open({
        title: 'Error', type: 'error', dismissBtn: true,
        message: `No se pudieron cargar los manuales. ${err?.message ?? ''}`,
      });
    }
  }

  selectRole(roleId: string): void {
    this.selectedRoleId.set(roleId);
  }

  // ─── PDF del manual ────────────────────────────────────────────────────

  /** Abre el PDF del rol seleccionado en una pestaña nueva. */
  async openPdf(): Promise<void> {
    const manual = this.selectedManual();
    const error = await openS3File(this.uploads, manual?.pdf);
    if (error) {
      await this.message.open({ title: 'Aviso', type: 'warning', message: error, dismissBtn: true });
    }
  }

  /** Abre un video en una pestaña nueva (enlace firmado de S3). */
  async openVideo(video: ManualVideo): Promise<void> {
    const error = await openS3File(this.uploads, video.key);
    if (error) {
      await this.message.open({ title: 'Aviso', type: 'warning', message: error, dismissBtn: true });
    }
  }

  /** Carga o reemplaza el PDF del manual del rol (solo admin). */
  uploadPdf(): void {
    const role = this.selectedRole();
    if (!this.canEdit() || !role) return;

    const manual = this.selectedManual();

    const ref = this.dialog.open(FileManagerDialogComponent, {
      width: '680px',
      maxHeight: '90vh',
      data: {
        value:       manual?.pdf ?? '',
        label:       `Manual de usuario — ${role.name}`,
        accept:      'application/pdf',
        uploadPath:  `manuals/${role.id}`,
        uploadFolio: 'pdf',
      },
    });

    ref.afterClosed().subscribe(async (key: string | null) => {
      if (key === null || key === undefined) return;   // cancelado
      await this.savePdf(role, key);
    });
  }

  private async savePdf(role: Role, key: string): Promise<void> {
    this.message.open({
      title: 'Espere', type: 'success', loading: true,
      message: 'Guardando el manual',
    });

    try {
      // `ensureForRole` crea el registro del rol la primera vez, para que
      // "subir el primer PDF" no tenga que distinguir alta de edición.
      const manual = await this.manualsService.ensureForRole(role.id, role.name);
      if (!manual) throw new Error('No se pudo preparar el manual del rol.');

      await this.manualsService.updateManual({
        id: manual.id,
        roleName: role.name,
        pdf: key || null,
        pdfName: key ? this.baseName(key) : null,
      });

      this.message.close();
      await this.loadManuals();
      await this.message.open({
        title: '¡Hecho!', type: 'success', duration: 2000,
        message: key ? 'Manual actualizado.' : 'Manual eliminado.',
      });
    } catch (err: any) {
      this.message.close();
      await this.message.open({
        title: 'Error', type: 'error', dismissBtn: true,
        message: `No se pudo guardar el manual. ${err?.message ?? ''}`,
      });
    }
  }

  // ─── Videos ────────────────────────────────────────────────────────────

  addVideo(): void {
    this.openVideoDialog(null);
  }

  editVideo(video: ManualVideo): void {
    this.openVideoDialog(video);
  }

  private openVideoDialog(video: ManualVideo | null): void {
    const role = this.selectedRole();
    if (!this.canEdit() || !role) return;

    const ref = this.dialog.open(ManualVideoDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      autoFocus: false,
      data: {
        modules:    this.moduleOptions(),
        roleName:   role.name,
        uploadPath: `manuals/${role.id}`,
        uploadedBy: this.usersService.currentUser()?.id ?? null,
        video,
      },
    });

    ref.afterClosed().subscribe(async (result: ManualVideo | undefined) => {
      if (!result) return;
      await this.saveVideo(role, result);
    });
  }

  private async saveVideo(role: Role, video: ManualVideo): Promise<void> {
    this.message.open({
      title: 'Espere', type: 'success', loading: true,
      message: 'Guardando el video',
    });

    try {
      const manual = await this.manualsService.ensureForRole(role.id, role.name);
      if (!manual) throw new Error('No se pudo preparar el manual del rol.');

      const current = manual.videos ?? [];
      const exists  = current.some(v => v.id === video.id);
      const videos  = exists
        ? current.map(v => (v.id === video.id ? video : v))
        : [...current, video];

      await this.manualsService.saveVideos(manual.id, videos);

      this.message.close();
      await this.loadManuals();
      await this.message.open({
        title: '¡Hecho!', type: 'success', duration: 2000,
        message: exists ? 'Video actualizado.' : 'Video agregado.',
      });
    } catch (err: any) {
      this.message.close();
      await this.message.open({
        title: 'Error', type: 'error', dismissBtn: true,
        message: `No se pudo guardar el video. ${err?.message ?? ''}`,
      });
    }
  }

  async deleteVideo(video: ManualVideo): Promise<void> {
    const manual = this.selectedManual();
    if (!this.canEdit() || !manual) return;

    const confirmed = await this.message.open({
      title: 'Confirmar',
      type: 'warning',
      message: `¿Quitar el video <b>${video.title}</b> del manual?`,
      acceptBtn: true,
      dismissBtn: true,
    });
    if (!confirmed) return;

    try {
      const videos = (manual.videos ?? []).filter(v => v.id !== video.id);
      await this.manualsService.saveVideos(manual.id, videos);

      // El archivo en S3 se borra después: si esto falla, el video ya
      // desapareció del manual y solo queda un huérfano en el bucket.
      try {
        await this.uploads.delete({
          path:     `manuals/${manual.role}`,
          folio:    'videos',
          filename: this.baseName(video.key),
        });
      } catch (err) {
        console.warn('[Manuales] El archivo quedó en S3:', err);
      }

      await this.loadManuals();
    } catch (err: any) {
      await this.message.open({
        title: 'Error', type: 'error', dismissBtn: true,
        message: `No se pudo quitar el video. ${err?.message ?? ''}`,
      });
    }
  }

  // ─── Helpers de vista ──────────────────────────────────────────────────

  manualFor(roleId: string): UserManual | null {
    return this.manuals()[roleId] ?? null;
  }

  hasPdf(roleId: string): boolean {
    return !!this.manuals()[roleId]?.pdf;
  }

  videosFor(roleId: string): number {
    return this.manuals()[roleId]?.videos?.length ?? 0;
  }

  goBack(): void {
    this.router.navigate(['/support/menu']);
  }

  private baseName(key: string): string {
    const parts = String(key).split('/');
    return parts[parts.length - 1] || key;
  }
}
